<template>
  <div class="repository-page">
    <!-- Page Title (no logo, just title) -->
    <header class="mz-header d-flex align-items-center justify-content-between px-3 py-2 mb-3">
      <h2 class="mb-0 page-title">
        Dépôts logistiques
      </h2>
      <b-button
        v-if="user.role === 'admin'"
        :to="{ name: 'repository-add' }"
        variant="primary"
        class="d-flex align-items-center"
        aria-label="Ajouter un nouveau dépôt logistique"
      >
        <feather-icon
          icon="PlusIcon"
          size="18"
        />
        <span class="ml-1 d-none d-md-inline">Ajouter un dépôt</span>
      </b-button>
    </header>

    <!-- Summary Cards Row (fixed root structure) -->
    <b-row
      class="mb-3 summary-cards"
      aria-label="Statistiques des dépôts logistiques"
      role="region"
    >
      <b-col
        lg="3"
        sm="6"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0 summary-card repo-stat-card repo-stat-purple">
          <div class="repo-stat-content">
            <div class="repo-stat-text">
              <p class="repo-stat-label mb-0">
                TOTAL DÉPÔTS
              </p>
              <h3 class="repo-stat-value mb-0">
                {{ summaryStats.total }}
              </h3>
            </div>
            <div
              class="repo-stat-icon"
              aria-hidden="true"
            >
              <feather-icon
                icon="HomeIcon"
                size="22"
                class="repo-stat-icon-svg"
              />
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col
        lg="3"
        sm="6"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0 summary-card repo-stat-card repo-stat-success">
          <div class="repo-stat-content">
            <div class="repo-stat-text">
              <p class="repo-stat-label mb-0">
                ACTIFS
              </p>
              <h3 class="repo-stat-value mb-0">
                {{ summaryStats.active }}
              </h3>
            </div>
            <div
              class="repo-stat-icon"
              aria-hidden="true"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="22"
                class="repo-stat-icon-svg"
              />
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col
        lg="3"
        sm="6"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0 summary-card repo-stat-card repo-stat-info">
          <div class="repo-stat-content">
            <div class="repo-stat-text">
              <p class="repo-stat-label mb-0">
                PERSONNEL
              </p>
              <h3 class="repo-stat-value mb-0">
                {{ summaryStats.personnel }}
              </h3>
            </div>
            <div
              class="repo-stat-icon"
              aria-hidden="true"
            >
              <feather-icon
                icon="UsersIcon"
                size="22"
                class="repo-stat-icon-svg"
              />
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col
        lg="3"
        sm="6"
        class="mb-2"
      >
        <b-card class="shadow-sm border-0 summary-card repo-stat-card repo-stat-warning">
          <div class="repo-stat-content">
            <div class="repo-stat-text">
              <p class="repo-stat-label mb-0">
                COMMANDES ACTIVES
              </p>
              <h3 class="repo-stat-value mb-0">
                {{ summaryStats.orders }}
              </h3>
            </div>
            <div
              class="repo-stat-icon"
              aria-hidden="true"
            >
              <feather-icon
                icon="PackageIcon"
                size="22"
                class="repo-stat-icon-svg"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Search and filter bar -->
    <b-card
      role="search"
      class="search-filter-bar mb-3"
    >
      <b-row class="flex-wrap align-items-end">
        <b-col
          cols="12"
          sm="6"
          md="6"
          lg="4"
          class="mb-2"
        >
          <b-form-group
            label="Recherche"
            label-for="search-input"
          >
            <b-input-group>
              <b-form-input
                id="search-input"
                v-model="searchTerm"
                placeholder="Nom, email ou téléphone..."
                aria-label="Recherche de dépôt logistique"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  aria-label="Lancer la recherche"
                  @click="getRepositories"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="6"
          lg="3"
          class="mb-2"
        >
          <b-form-group label="Statut">
            <b-form-select
              v-model="statusFilter"
              :options="[
                { value: null, text: 'Tous les statuts' },
                { value: true, text: 'Actif' },
                { value: false, text: 'Inactif' }
              ]"
              @input="getRepositories"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="6"
          lg="3"
          class="mb-2"
        >
          <b-form-group label="Location">
            <b-form-select
              v-model="rentFilter"
              :options="[
                { value: null, text: 'Tous' },
                { value: true, text: 'En location' },
                { value: false, text: 'Non loué' }
              ]"
              @input="getRepositories"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="6"
          lg="2"
          class="mb-2 d-flex align-items-end"
        >
          <div class="d-flex align-items-center w-100 justify-content-end filter-action-bar">
            <b-button
              v-b-tooltip.hover
              title="Réinitialiser les filtres"
              variant="outline-secondary"
              class="action-icon-btn mr-1"
              @click="resetFilters"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="16"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Exporter CSV"
              variant="outline-success"
              class="action-icon-btn"
              aria-label="Exporter CSV"
              @click="exportToCSV"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
      <div
        v-if="hasActiveFilters"
        class="active-filters mt-2"
      >
        <span class="text-muted mr-2">Filtres actifs:</span>
        <b-badge
          v-if="statusFilter !== null"
          variant="light-primary"
          class="mr-2 filter-badge"
        >
          Statut: {{ statusFilter ? 'Actif' : 'Inactif' }}
          <feather-icon
            icon="XIcon"
            size="12"
            class="ml-1 cursor-pointer"
            @click="clearStatusFilter"
          />
        </b-badge>
        <b-badge
          v-if="rentFilter !== null"
          variant="light-primary"
          class="mr-2 filter-badge"
        >
          {{ rentFilter ? 'En location' : 'Non loué' }}
          <feather-icon
            icon="XIcon"
            size="12"
            class="ml-1 cursor-pointer"
            @click="clearRentFilter"
          />
        </b-badge>
        <b-badge
          v-if="searchTerm"
          variant="light-primary"
          class="mr-2 filter-badge"
        >
          Recherche: "{{ searchTerm }}"
          <feather-icon
            icon="XIcon"
            size="12"
            class="ml-1 cursor-pointer"
            @click="clearSearchTerm"
          />
        </b-badge>
      </div>
    </b-card>
    <!-- ...existing code for skeletons, empty state, card/table view, modal... -->
    <!-- Only one summary cards row and one search/filter bar should exist. Removed duplicates. -->

    <!-- Skeleton Loading Placeholders -->
    <div v-if="isLoading === true">
      <b-card
        v-for="n in 4"
        :key="`skeleton-${n}`"
        class="mb-1"
      >
        <b-skeleton-wrapper>
          <b-row>
            <b-col cols="8">
              <div class="d-flex">
                <b-skeleton
                  variant="primary"
                  type="avatar"
                  animation="wave"
                  class="mr-2"
                />
                <div class="flex-grow-1">
                  <b-skeleton width="60%" />
                  <b-skeleton
                    width="40%"
                    class="mt-1"
                  />
                </div>
              </div>
            </b-col>
            <b-col cols="2">
              <b-skeleton
                width="50px"
                height="24px"
                animation="wave"
              />
            </b-col>
            <b-col
              cols="2"
              class="text-right"
            >
              <b-skeleton
                width="90px"
                height="32px"
                animation="wave"
              />
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </b-card>
    </div>

    <!-- Empty state -->
    <b-card
      v-else-if="repositories.length === 0"
      class="text-center py-5 empty-state-card"
      aria-live="polite"
      aria-label="Aucun dépôt logistique trouvé"
    >
      <div class="empty-state-logo mb-3">
        <brand-logo
          height="80"
          class="opacity-25"
        />
      </div>
      <h4 id="empty-repo-title">
        Aucun dépôt logistique trouvé
      </h4>
      <p
        id="empty-repo-desc"
        class="text-muted"
      >
        Modifiez vos critères de recherche ou créez un nouveau dépôt logistique pour démarrer la gestion de vos entrepôts.
      </p>
      <b-button
        v-if="user.role === 'admin'"
        :to="{ name: 'repository-add' }"
        variant="primary"
        class="mt-2"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
        />
        <span class="ml-1">Ajouter un dépôt</span>
      </b-button>
    </b-card>

    <!-- Card View Mode -->
    <div
      v-else-if="viewMode === 'card' && !isLoading && repositories.length > 0"
      aria-label="Vue cartes des dépôts logistiques"
      role="region"
    >
      <transition-group
        name="repository-card-list"
        tag="div"
        class="repository-card-grid"
      >
        <b-card
          v-for="repo in paginatedRepositories"
          :key="repo.id"
          class="repository-card mb-3 position-relative"
          no-body
          :aria-label="`Carte du dépôt logistique ${repo.name}`"
        >
          <!-- Repository status indicator -->
          <div
            v-b-tooltip.hover
            class="status-indicator"
            :class="repo.is_active ? 'bg-danger' : 'bg-success'"
            :title="repo.is_active ? 'Actif' : 'Inactif'"
          />

          <b-card-body>
            <div class="d-flex align-items-center mb-2">
              <div
                :class="['repo-avatar', getBgColorClass(repo.is_active)]"
                aria-hidden="true"
              >
                <feather-icon
                  icon="HomeIcon"
                  :class="getTextColorClass(repo.is_active)"
                  size="22"
                />
              </div>
              <div class="ml-2">
                <h5
                  class="mb-0"
                  :aria-label="'Nom du dépôt logistique : ' + repo.name"
                >
                  {{ repo.name }}
                </h5>
                <small
                  v-if="repo.address"
                  class="text-muted d-block mt-1"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="12"
                    class="mr-1"
                  />
                  {{ truncateAddress(repo.address) }}
                </small>
              </div>
            </div>

            <div class="repository-stats">
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="UsersIcon"
                    size="14"
                    class="text-primary"
                  />
                </div>
                <span>{{ repo.manager_count || 0 }}</span>
                <small>Managers</small>
              </div>

              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="TruckIcon"
                    size="14"
                    class="text-success"
                  />
                </div>
                <span>{{ repo.delivery_man_count || 0 }}</span>
                <small>Livreurs</small>
              </div>

              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                    class="text-warning"
                  />
                </div>
                <span>{{ repo.active_orders_count || 0 }}</span>
                <small>Commandes</small>
              </div>
            </div>

            <div
              v-if="repo.is_under_rent"
              class="rental-info mt-2"
            >
              <b-badge
                variant="light-info"
                class="px-2 py-1"
              >
                <feather-icon
                  icon="HomeIcon"
                  size="12"
                  class="mr-1"
                />
                En location
              </b-badge>
            </div>
          </b-card-body>

          <b-card-footer class="d-flex justify-content-between align-items-center py-2">
            <small class="text-muted">ID: {{ repo.id }}</small>

            <div class="card-actions">
              <b-dropdown
                right
                variant="link"
                no-caret
                toggle-class="p-0"
                menu-class="dropdown-menu-right"
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body"
                  />
                </template>

                <b-dropdown-item :to="{ name: 'repository-show', params: { id: repo.id }}">
                  <feather-icon
                    icon="EyeIcon"
                    size="16"
                    class="mr-2"
                  />
                  <span>Voir détails</span>
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="user.role === 'admin'"
                  :to="{ name: 'repository-edit', params: { id: repo.id }}"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                    class="mr-2"
                  />
                  <span>Modifier</span>
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="user.role === 'admin'"
                  @click="confirmToggleStatus(repo)"
                >
                  <feather-icon
                    :icon="repo.is_active ? 'XIcon' : 'CheckIcon'"
                    size="16"
                    class="mr-2"
                  />
                  <span>{{ repo.is_active ? 'Désactiver' : 'Activer' }}</span>
                </b-dropdown-item>
              </b-dropdown>

              <b-button
                variant="primary"
                size="sm"
                class="btn-icon rounded-circle ml-1"
                :to="{ name: 'repository-show', params: { id: repo.id }}"
              >
                <feather-icon
                  icon="ArrowRightIcon"
                  size="16"
                />
              </b-button>
            </div>
          </b-card-footer>
        </b-card>
      </transition-group>

      <!-- Pagination for card view -->
      <div class="d-flex justify-content-center mt-2">
        <b-pagination
          v-model="currentPage"
          :total-rows="repositories.length"
          :per-page="pageLength"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>
    </div>

    <!-- Status toggle confirmation modal -->
    <b-modal
      id="confirm-status-modal"
      ref="confirmStatusModal"
      :title="statusModalTitle"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
      @cancel="cancelStatusChange"
    >
      <p>{{ statusModalMessage }}</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormInput, BFormSelect, BButton, BDropdown,
  BDropdownItem, BRow, BCol, BCard, BBadge, BModal, BInputGroup,
  BInputGroupAppend, BSkeletonWrapper, BSkeleton, BCardBody, BCardFooter,
} from 'bootstrap-vue'

import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

export default {
  components: {
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BRow,
    BCol,
    BCard,
    BBadge,
    BModal,
    BInputGroup,
    BInputGroupAppend,
    BDropdown,
    BDropdownItem,
    BSkeletonWrapper,
    BSkeleton,
    BCardBody,
    BCardFooter,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 25,
      dir: false,
      viewMode: 'card', // 'table' or 'card'
      currentPage: 1,
      columns: [
        {
          label: 'Nom',
          field: 'name',
          sortable: true,
        },
        {
          label: 'Adresse',
          field: 'address',
          sortable: true,
        },
        {
          label: 'Gouvernorats',
          field: 'governorate_names',
          sortable: false,
        },
        {
          label: 'Managers',
          field: 'manager_count',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Livreurs',
          field: 'delivery_man_count',
          type: 'number',
          sortable: true,
        },
        {
          label: 'N° Commandes par mois',
          field: 'active_orders_count',
          type: 'number',
          sortable: true,
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: true,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
          width: '150px',
        },
      ],
      repositories: [],
      user: {},
      searchTerm: '',
      statusFilter: null,
      rentFilter: null,
      selectedRepository: null,
      statusModalTitle: '',
      statusModalMessage: '',
      searchTimer: null,
      summaryStats: {
        total: 0,
        active: 0,
        personnel: 0,
        orders: 0,
      },
    }
  },

  computed: {
    hasActiveFilters() {
      return this.statusFilter !== null || this.rentFilter !== null || this.searchTerm !== ''
    },

    // Paginated repositories for card view
    paginatedRepositories() {
      if (this.viewMode !== 'card') return this.repositories

      const startIndex = (this.currentPage - 1) * this.pageLength
      const endIndex = startIndex + this.pageLength
      return this.repositories.slice(startIndex, endIndex)
    },
  },

  watch: {
    searchTerm() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }

      this.searchTimer = setTimeout(() => {
        this.getRepositories()
      }, 500)
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.getRepositories()
  },

  mounted() {
    this.$nextTick(() => {
      this.forceTableStyleUpdate()
    })
  },

  activated() {
    this.$nextTick(() => {
      this.forceTableStyleUpdate()
    })
  },

  methods: {
    async getRepositories() {
      this.isLoading = true
      try {
        const params = {
          is_under_rent: this.rentFilter !== null ? this.rentFilter : undefined,
          is_active: this.statusFilter !== null ? this.statusFilter : undefined,
          name__icontains: this.searchTerm || undefined,
          page_size: 500,
        }

        // Remove undefined values to avoid sending them as query parameters
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key])

        const { data } = await axios.get('/api/repositories/list/', { params })

        // Handle both paginated and non-paginated responses
        const repositories = Array.isArray(data) ? data : (data.results || [])
        this.repositories = repositories
        this.calculateSummaryStats(repositories)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            icon: 'AlertCircleIcon',
            text: 'Erreur lors du chargement des dépôts',
            variant: 'danger',
          },
        }, {
          position: 'top-right',
        })
      } finally {
        this.isLoading = false
      }
    },

    calculateSummaryStats(data) {
      // Calculate summary statistics
      this.summaryStats = {
        total: data.length,
        active: data.filter(repo => repo.is_active).length,
        personnel: data.reduce((sum, repo) => sum + (repo.manager_count || 0) + (repo.delivery_man_count || 0), 0),
        orders: data.reduce((sum, repo) => sum + (repo.active_orders_count || 0), 0),
      }
    },

    confirmToggleStatus(repository) {
      this.selectedRepository = repository
      this.statusModalTitle = repository.is_active ? 'Désactiver le dépôt' : 'Activer le dépôt'
      this.statusModalMessage = repository.is_active
        ? 'Êtes-vous sûr de vouloir désactiver ce dépôt? Cette action pourrait affecter les opérations en cours.'
        : 'Êtes-vous sûr de vouloir activer ce dépôt?'

      this.$refs.confirmStatusModal.show()
    },

    async handleStatusConfirmed() {
      if (!this.selectedRepository) return

      try {
        await axios.post(`/api/repositories/toggle-status/${this.selectedRepository.id}/`)

        // Update the repository status locally
        const index = this.repositories.findIndex(repo => repo.id === this.selectedRepository.id)
        if (index !== -1) {
          this.repositories[index].is_active = !this.repositories[index].is_active
          this.repositories[index].active_status = this.repositories[index].is_active ? 'Actif' : 'Inactif'

          // Update summary stats
          this.calculateSummaryStats(this.repositories)
        }

        this.showToast(
          'success',
          'top-center',
          `Le dépôt a été ${!this.selectedRepository.is_active ? 'activé' : 'désactivé'} avec succès`,
        )
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du changement de statut')
      } finally {
        this.selectedRepository = null
      }
    },

    cancelStatusChange() {
      this.selectedRepository = null
    },

    resetFilters() {
      this.searchTerm = ''
      this.statusFilter = null
      this.rentFilter = null
      this.getRepositories()
    },

    exportToCSV() {
      try {
        // Format repository data for CSV
        const csvData = this.repositories.map(repo => ({
          Nom: repo.name || '',
          Email: repo.email || '',
          Téléphone: repo.phone || '',
          Adresse: repo.address || '',
          'Espace disponible': repo.available_space || '',
          Managers: repo.manager_count || 0,
          Livreurs: repo.delivery_man_count || 0,
          'Commandes Actives': repo.active_orders_count || 0,
          'En location': repo.is_under_rent ? 'Oui' : 'Non',
          'Loyer mensuel': repo.monthly_rent_amount || '',
          Statut: repo.is_active ? 'Actif' : 'Inactif',
          'Créé le': repo.created_at ? format(new Date(repo.created_at), 'dd MMMM yyyy', { locale: fr }) : '',
        }))

        // Convert to CSV string
        const headers = Object.keys(csvData[0]).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download without file-saver
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

        // Create a temporary anchor element and trigger download
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `depots_${format(new Date(), 'yyyy-MM-dd')}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error exporting to CSV:', error)
        this.showToast('danger', 'top-right', 'Erreur lors de l\'exportation des données')
      }
    },

    // toast
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Notification',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },

    forceTableStyleUpdate() {
      const tableElement = document.querySelector('.vgt-table')
      if (tableElement) {
        // Force a reflow by reading a property that causes layout calculation
        // eslint-disable-next-line no-unused-expressions
        tableElement.offsetHeight
        tableElement.classList.add('force-update')
        setTimeout(() => {
          tableElement.classList.remove('force-update')
        }, 0)
      }
    },

    // Get background color class based on repository status
    getBgColorClass(isActive) {
      return isActive ? 'bg-light-primary' : 'bg-light-danger'
    },

    // Get text color class based on repository status
    getTextColorClass(isActive) {
      return isActive ? 'text-primary' : 'text-danger'
    },

    clearStatusFilter() {
      this.statusFilter = null
      this.getRepositories()
    },

    clearRentFilter() {
      this.rentFilter = null
      this.getRepositories()
    },

    clearSearchTerm() {
      this.searchTerm = ''
      // getRepositories() will be triggered by the watcher
    },

    truncateAddress(address) {
      if (!address) return ''
      return address.length > 30 ? `${address.substring(0, 30)}...` : address
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.mz-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  box-shadow: 0 2px 8px 0 rgba(34, 41, 47, 0.04);
  min-height: 64px;
}
.mz-logo {
  height: 44px;
  width: auto;
  margin-right: 0.5rem;
}
.page-title {
  font-family: 'Montserrat', 'Nunito Sans', Arial, sans-serif;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.5px;
}
.repository-page .summary-cards .summary-card {
  border-radius: 0.75rem;
  min-height: 100px;
  background: #fff;
  box-shadow: 0 2px 8px 0 rgba(34, 41, 47, 0.06);
}
.search-filter-bar {
  border-radius: 0.75rem;
  background: #fafbfc;
  box-shadow: 0 2px 8px 0 rgba(34, 41, 47, 0.03);
}
.empty-state-card {
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px 0 rgba(34, 41, 47, 0.04);
}
.empty-state-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  background-color: rgba(237, 28, 36, 0.08); // logo red
  border-radius: 50%;
}
.opacity-25 {
  opacity: 0.25;
}
.repository-table {
  .vgt-table {
    border: none !important;
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
    background-color: white !important;
    th {
      font-weight: 600 !important;
      background-color: #f8f8f8 !important;
      padding: 0.75rem 1rem !important;
      text-transform: uppercase !important;
      font-size: 0.85rem !important;
      letter-spacing: 0.5px !important;
      color: #222 !important;
    }
    td {
      padding: 0.75rem 1rem !important;
      vertical-align: middle !important;
      border-bottom: 1px solid #ebe9f1 !important;
    }
    tr:hover {
      background-color: rgba(237, 28, 36, 0.04) !important; // logo red
    }
  }
  .vgt-wrap__footer {
    border: none !important;
    background-color: white !important;
    padding: 1rem !important;
    box-shadow: 0 -2px 10px 0 rgba(34, 41, 47, 0.05) !important;
  }
  .vgt-global-search {
    background-color: white !important;
    border: none !important;
    padding: 1.41rem 1rem !important;
  }
  .avatar-tag {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 32px !important;
    width: 32px !important;
  }
}
.repository-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}
.repository-card {
  border-radius: 0.5rem;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 4px 10px rgba(34, 41, 47, 0.1);
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(237, 28, 36, 0.15); // logo red
  }
  .status-indicator {
    position: absolute;
    right: 0;
    top: 0;
    width: 10px;
    height: 100%;
  }
  .repo-avatar {
    width: 48px;
    height: 48px;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .repository-stats {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    .stat-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      span {
        font-weight: 600;
        font-size: 1.25rem;
        line-height: 1;
      }
      small {
        color: #6e6b7b;
        font-size: 0.7rem;
      }
      .stat-icon {
        background: #f8f8f8;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 0.25rem;
      }
    }
  }
  .card-actions {
    display: flex;
    align-items: center;
  }
}
.repository-card-list-enter-active, .repository-card-list-leave-active {
  transition: all 0.5s;
}
.repository-card-list-enter, .repository-card-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.active-filters {
  .filter-badge {
    font-weight: normal;
    svg {
      cursor: pointer;
      transition: transform 0.2s;
      &:hover {
        transform: scale(1.2);
      }
    }
  }
}
.cursor-pointer {
  cursor: pointer;
}
// Extra style for compact action bar
.filter-action-bar .action-icon-btn {
  min-width: 36px;
  padding: 0.375rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss" scoped>
.counter-cell {
  display: flex;
  align-items: center;

  .counter-icon {
    margin-right: 0.5rem;
    opacity: 0.8;
  }

  .counter-value {
    font-weight: 500;
  }
}

.repo-stat-card {
  --repo-accent: #7367f0;
  --repo-bg: rgba(115, 103, 240, 0.10);
  --repo-icon-bg: linear-gradient(135deg, #7367f0 0%, #5f55ee 100%);

  border-radius: 18px;
  border: 1px solid rgba(17, 24, 39, 0.06);
  background: var(--repo-bg);
  box-shadow: 0 10px 28px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  position: relative;

  .card-body {
    padding: 1.35rem 1.4rem;
  }

  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    right: -52px;
    bottom: -60px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 70%);
    pointer-events: none;
  }
}

.repo-stat-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.repo-stat-text {
  min-width: 0;
}

.repo-stat-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--repo-accent);
  opacity: 0.95;
}

.repo-stat-value {
  margin-top: 0.35rem;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
  color: #111827;
}

.repo-stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: var(--repo-icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 18px 30px rgba(17, 24, 39, 0.18);
  transform: translateY(-6px);
}

.repo-stat-icon-svg {
  color: #ffffff;
}

.repo-stat-purple {
  --repo-accent: #7367f0;
  --repo-bg: rgba(115, 103, 240, 0.10);
  --repo-icon-bg: linear-gradient(135deg, #7367f0 0%, #5f55ee 100%);
}

.repo-stat-success {
  --repo-accent: #28c76f;
  --repo-bg: rgba(40, 199, 111, 0.10);
  --repo-icon-bg: linear-gradient(135deg, #28c76f 0%, #1fb565 100%);
}

.repo-stat-info {
  --repo-accent: #00cfe8;
  --repo-bg: rgba(0, 207, 232, 0.10);
  --repo-icon-bg: linear-gradient(135deg, #00cfe8 0%, #00b8cf 100%);
}

.repo-stat-warning {
  --repo-accent: #ff9f43;
  --repo-bg: rgba(255, 159, 67, 0.10);
  --repo-icon-bg: linear-gradient(135deg, #ff9f43 0%, #ff851a 100%);
}

.action-buttons {
  .btn-icon {
    width: 34px;
    height: 34px;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    transition: transform 0.15s ease, background 0.15s ease;

    &:hover {
      transform: translateY(-2px);
    }

    &.btn-flat-primary:hover {
      background-color: rgba(115, 103, 240, 0.12);
    }

    &.btn-flat-secondary:hover {
      background-color: rgba(130, 134, 139, 0.12);
    }

    &.btn-flat-success:hover {
      background-color: rgba(40, 199, 111, 0.12);
    }

    &.btn-flat-danger:hover {
      background-color: rgba(234, 84, 85, 0.12);
    }
  }
}

// Enhanced styling for summary cards
.avatar {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.empty-state-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  background-color: rgba(115, 103, 240, 0.08);
  border-radius: 50%;
}

.opacity-25 {
  opacity: 0.25;
}
</style>
